import {css, StyleSheet} from 'aphrodite';
import React, {useMemo, useState} from 'react';
import {invert} from 'underscore';

import {assert} from 'Asserts';
import {
  DataPolicyCollaborator,
  isActorCollaborator,
  isDataPolicyActorCollaborator,
  isDataPolicyGroupCollaborator,
  Owner,
  OwnerInfo,
} from 'auth/PermissionConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Entity, EntityType, isUser} from 'constants/UserConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  SavedSearchSharingWarningsQuery,
  SavedSearchSharingWarningsQueryVariables,
} from 'search/saved-searches/__generated/SavedSearchSharingWarningsQuery';
import {OwnerCollaboratorRow, CollaboratorRow} from 'search/saved-searches/SavedSearchCollaborators';
import {SAVED_SEARCH_SHARING_WARNINGS_QUERY} from 'search/saved-searches/SavedSearchesQuery';
import {LEGACY_FONT_PROXIMA_13} from 'styles/FontConstants';
import Avatar from 'ui-library/avatar/Avatar';
import {AvatarShape, AvatarSize} from 'ui-library/avatar/AvatarConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {BLUE} from 'ui-library/Colors';
import {SingleEntityInput} from 'ui-library/entity-input/SingleEntityInput';
import {TagsInputSize} from 'ui-library/tags-input/TagsInputConstants';

import {SavedSearchPermissionsGraphQL} from '../../../graphql-fragments/search/__generated/SavedSearchPermissionsGraphQL';
import {SavedSearchPermissionLevel, setDataPolicyId} from './SavedSearchSharingUtil';

export interface SavedSearchSharingPaneProps {
  savedSearchId: string;
  collaborators: Array<DataPolicyCollaborator>;
  onChangeCollaborators: (newCollaborators: Array<DataPolicyCollaborator>) => void;
  permissions: SavedSearchPermissionsGraphQL;
  permissionLevelToDataPolicyId: {[level in SavedSearchPermissionLevel]: string};
  owner: Owner;
  ownerInfo: OwnerInfo;
  onChangeOwnerInfo: (newOwnerInfo: OwnerInfo) => void;
  autoFocus?: boolean;
}

export default function SavedSearchSharingPane({
  savedSearchId,
  collaborators,
  onChangeCollaborators,
  permissions,
  permissionLevelToDataPolicyId,
  owner,
  ownerInfo,
  onChangeOwnerInfo,
  autoFocus,
}: SavedSearchSharingPaneProps): JSX.Element {
  const [showAllUsers, setShowAllUsers] = useState(false);
  const collaboratorIds = useMemo(() => new Set(collaborators.map((c) => c.id)), [collaborators]);
  const idsToCheckAuth =
    owner.type === ModelTypeConstants.ORGANIZATION && ownerInfo.memberDataPolicyId
      ? Array.from(collaboratorIds).concat(owner.id)
      : Array.from(collaboratorIds);
  const unauthorizedCollaborators = useCollaboratorWarnings(savedSearchId, idsToCheckAuth);
  const dataPolicyIdToPermissionLevel = useMemo(
    () => invert(permissionLevelToDataPolicyId) as {[dataPolicyId: string]: SavedSearchPermissionLevel},
    [permissionLevelToDataPolicyId]
  );

  const displayedCollaborators = showAllUsers ? collaborators : collaborators.slice(0, 2);

  if (owner.type === ModelTypeConstants.ORGANIZATION) {
    const dataPolicyId = ownerInfo.memberDataPolicyId;
    if (dataPolicyId) {
      const memberPermissionLevel = dataPolicyIdToPermissionLevel[dataPolicyId];
      assert(memberPermissionLevel != null, 'Saved search owner info (org) has invalid data policy ID', {
        dataPolicyId,
        permissionLevelToDataPolicyId,
      });
    }
  }

  return (
    <>
      <h3 className={css(styles.heading)}>Sharing</h3>
      <p>Users with access can share their access with others.</p>
      <SingleEntityInput
        placeholder="Add people, teams, and orgs..."
        filterOptions={(_, options) =>
          options.filter(
            (option) =>
              !collaboratorIds.has(option.id) &&
              (option.id !== owner.id ||
                // If owner is an org, and the members don't have access, a user should be able to find it in the input to add member access
                (owner.type === ModelTypeConstants.ORGANIZATION && ownerInfo.memberDataPolicyId == null))
          )
        }
        onSubmit={({entity}) => {
          if (entity == null) {
            return;
          }
          const view = permissionLevelToDataPolicyId[SavedSearchPermissionLevel.VIEW];
          if (entity.id === owner.id) {
            assert(
              owner.type === ModelTypeConstants.ORGANIZATION,
              'Owner should not be able to be added, unless it is an organization'
            );

            onChangeOwnerInfo({
              ...ownerInfo,
              memberDataPolicyId: view,
            });
          } else {
            const newCollabs = [
              setDataPolicyId(entity as Entity, {dataPolicyId: view, adminDataPolicyId: view}),
              ...collaborators,
            ];
            onChangeCollaborators(newCollabs);
          }
        }}
        size={TagsInputSize.SMALL}
        autoFocus={autoFocus}
        enableNewEntityInput={true}
      />
      <h5 style={{fontSize: '1.2em'}} className={css(styles.heading)}>
        Shared with
      </h5>
      <div className={css(styles.collaborators)}>
        <OwnerCollaboratorRow
          owner={owner}
          ownerInfo={ownerInfo}
          permissions={permissions}
          permissionLevelToDataPolicyId={permissionLevelToDataPolicyId}
          onChangeOwnerInfo={onChangeOwnerInfo}
          memberPermissionLevel={
            ownerInfo.memberDataPolicyId ? dataPolicyIdToPermissionLevel[ownerInfo.memberDataPolicyId] : null
          }
          memberWarning={displayWarning(unauthorizedCollaborators, owner.id, 1)}
          noBorder={displayedCollaborators.length === 0}
        />

        {displayedCollaborators.map((collaborator, index) => {
          const dataPolicyId = isDataPolicyActorCollaborator(collaborator)
            ? collaborator.dataPolicyId
            : collaborator.memberDataPolicyId;
          assert(dataPolicyId != null, 'Saved search collaborator is missing data policy ID', {
            collaborator,
          });

          const permissionLevel = dataPolicyIdToPermissionLevel[dataPolicyId];
          assert(permissionLevel != null, 'Saved search collaborator has invalid data policy ID', {
            dataPolicyId,
            permissionLevelToDataPolicyId,
          });

          const adminDataPolicyId = isDataPolicyGroupCollaborator(collaborator)
            ? collaborator.adminDataPolicyId
            : dataPolicyId;
          assert(adminDataPolicyId != null, 'Saved search collaborator is missing data policy ID', {
            collaborator,
          });
          const adminPermissionLevel = dataPolicyIdToPermissionLevel[adminDataPolicyId];
          assert(adminPermissionLevel != null, 'Saved search collaborator has invalid data policy ID', {
            dataPolicyId,
            permissionLevelToDataPolicyId,
          });

          return (
            <CollaboratorRow
              key={collaborator.id}
              collaborator={collaborator}
              permissionLevel={permissionLevel}
              onChangeCollaborators={onChangeCollaborators}
              collaborators={collaborators}
              permissionLevelToDataPolicyId={permissionLevelToDataPolicyId}
              permissions={permissions}
              memberWarning={displayWarning(
                unauthorizedCollaborators,
                collaborator.id,
                !isActorCollaborator(collaborator) ? 1 : null
              )}
              adminWarning={displayWarning(unauthorizedCollaborators, collaborator.id, 0)}
              adminPermissionLevel={adminPermissionLevel}
              noBorder={
                index === displayedCollaborators.length - 1 && (showAllUsers || collaborators.length <= 2)
              }
            />
          );
        })}
      </div>

      {!showAllUsers && collaborators.length > 2 && (
        <AdditionalCollaboratorsRow
          additionalCollaborators={collaborators.slice(2)}
          setShowAllUsers={setShowAllUsers}
        />
      )}

      {unauthorizedCollaborators.length > 0 && (
        <div className={css(styles.warningBanner)}>
          <InlineBanner
            bannerType={InlineBannerType.WARNING}
            text="Some collaborators may be unable to run this search."
          />
        </div>
      )}
    </>
  );
}

export interface AdditionalUsersRowProps {
  additionalCollaborators: Array<DataPolicyCollaborator>;
  setShowAllUsers: (show: boolean) => void;
}

function AdditionalCollaboratorsRow(props: AdditionalUsersRowProps): JSX.Element {
  const others = `+${props.additionalCollaborators.length} others`;
  const additionalDisplayedCollaborators = props.additionalCollaborators.slice(0, 5);
  return (
    <div className={css(rowStyles.wholeRow)}>
      <div>
        {additionalDisplayedCollaborators.map((collaborator) => (
          <Avatar
            key={collaborator.id}
            className={css(rowStyles.avatar)}
            entity={collaborator}
            size={AvatarSize.SMALL}
            shape={
              isUser(collaborator as {type?: EntityType | undefined})
                ? AvatarShape.CIRCLE
                : AvatarShape.SQUARE
            }
            showTooltip={false}
          />
        ))}
      </div>
      <div className={css(rowStyles.numberOfAdditionalCollaborators)}>{others}</div>
      <div
        onClick={() => {
          props.setShowAllUsers(true);
        }}
        className={css(rowStyles.show)}
      >
        show
      </div>
    </div>
  );
}

export interface UnauthorizedCollaborator {
  apiId: string;
  role: number | null;
}

function useCollaboratorWarnings(
  savedSearchId: string,
  collaboratorIds: Array<string>
): Array<UnauthorizedCollaborator> {
  const savedSearchWarning = useBenchlingQuery<
    SavedSearchSharingWarningsQuery,
    SavedSearchSharingWarningsQueryVariables
  >(SAVED_SEARCH_SHARING_WARNINGS_QUERY, {
    variables: {id: savedSearchId, collaboratorIds},
    renderCachedDataWhenRefetching: true,
    fetchPolicy: 'cache-and-network',
  });

  let unauthorizedCollaborators: Array<UnauthorizedCollaborator> = [];
  if (savedSearchWarning.type === QueryResultType.SUCCESS) {
    let currentSavedSearch: SavedSearchSharingWarningsQuery['savedSearch'];

    // displaying the previousData when full data isn't loaded yet to prevent the warnings symbols
    // from disappearing and coming back
    if (savedSearchWarning.loading && savedSearchWarning.previousData) {
      currentSavedSearch = savedSearchWarning.previousData.savedSearch!;
    } else {
      currentSavedSearch = savedSearchWarning.data.savedSearch!;
    }

    if (currentSavedSearch && isInaccessible(currentSavedSearch)) {
      throw new Error('Saved Search is inaccessible');
    }

    if (
      currentSavedSearch &&
      currentSavedSearch.__typename === 'SavedSearchNode' &&
      currentSavedSearch.unauthorizedCollaborators != null
    ) {
      unauthorizedCollaborators = currentSavedSearch.unauthorizedCollaborators;
    }
  }

  return unauthorizedCollaborators;
}

function displayWarning(
  unauthorizedCollaborators: Array<UnauthorizedCollaborator>,
  collaboratorId: string,
  role: number | null
): boolean {
  assert(role == null || role === 0 || role === 1, 'Role in group permissions can only be null, 0, 1');
  for (const collaborator of unauthorizedCollaborators) {
    if (collaborator.apiId === collaboratorId && collaborator.role === role) {
      return true;
    }
  }
  return false;
}

const rowStyles = StyleSheet.create({
  wholeRow: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '10px',
  },
  avatar: {
    marginRight: '-0.6em',
  },
  numberOfAdditionalCollaborators: {
    marginLeft: '1em',
    ...LEGACY_FONT_PROXIMA_13,
  },
  show: {
    marginLeft: 4,
    ...LEGACY_FONT_PROXIMA_13,
    color: BLUE,
    cursor: 'pointer',
  },
});

const styles = StyleSheet.create({
  heading: {
    margin: '1.75rem 0 0.5rem',
  },
  collaborators: {
    display: 'grid',
    gridTemplateRows: 'auto',
    alignItems: 'center',
    gap: '0.5em',
    marginTop: '1em',
    maxHeight: '242px',
    overflow: 'auto',
  },
  warningBanner: {
    marginTop: '1.5em',
  },
});
